import {
  BackEndValueType,
  DynamicTypes,
  ECardinality,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IKType
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import isEqual from "react-fast-compare";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { knownTypes } from "../../../../shared/utility/designer-symbol-picker-engine";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import ActionParameterTitle from "./action-parameter-title";
import SymbolDropdownLoading from "./symbol-dropdown-loading";

export interface IGetFromBagSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  onSaveAction: (action: IAction, changeReason?: ESdChangeReason) => void;
  /**
   * If AddToBag action is in the same screen, it will be passed here
   * So we can update the return type of GetFromBag actions when AddToBag action is updated
   */
  addToBagAction: IAction | null;
}

export interface IGetFromBagSymbolsGroupState {}

class GetFromBagSymbolsGroup extends PureSdComponent<
  IGetFromBagSymbolsGroupProps & IWithSDProps & IWithContextProp,
  IGetFromBagSymbolsGroupState
> {
  constructor(props: IGetFromBagSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {};
  }

  async componentDidMount() {
    if (!this.props.addToBagAction) {
      await this.props.sd.fetchDynamicTypeKeys();
      this.updateReturnTypesFromBagKey(this.props.sd.bags);
    }
  }

  async componentDidUpdate(prevProps: IGetFromBagSymbolsGroupProps) {
    if (
      this.props.addToBagAction &&
      this.getBagKeyValue(this.props.parameters) === this.getBagKeyValue(this.props.addToBagAction.parameters) &&
      !isEqual(prevProps.addToBagAction?.parameters, this.props.addToBagAction?.parameters)
    ) {
      try {
        const { bags } = await this.props.sd.getLocalDynamicTypes(true);
        this.updateReturnTypesFromBagKey(bags);
      } catch (error) {
        console.error("Failed to update bags in componentDidUpdate:", error);
      }
    }
  }

  /**
   * Navigate through typeModel using path to get the nested type model
   * Similar to backend's GetReturnTypeModelByFieldName
   */
  private getTypeModelByPath = (typeModel: IKType | null | undefined, path: string | null): IKType | undefined => {
    if (!typeModel || !path) return typeModel || undefined;

    const fields = path.split(".");
    if (fields.length === 0) return typeModel;

    let currentModel = typeModel;
    for (let i = 0; i < fields.length; i++) {
      const fieldNameWithoutModifier = fields[i].split("[")[0];
      const field = currentModel.elements?.find((x) => x.name === fieldNameWithoutModifier);

      if (!field) return undefined;

      if (i === fields.length - 1) {
        return field.elementTypeModel;
      }

      if (field.elementTypeModel) {
        currentModel = field.elementTypeModel;
      } else {
        return undefined;
      }
    }

    return currentModel;
  };

  updateReturnTypesFromBagKey = (bags: DynamicTypes) => {
    const bagKeyValue = this.getBagKeyValue(this.props.parameters);
    if (!bagKeyValue || !bags[bagKeyValue]) return;

    const action: IAction = _.cloneDeep(this.props.action);
    const bagInfo = bags[bagKeyValue];

    const resolvedTypeModel = bagInfo.path
      ? this.getTypeModelByPath(bagInfo.typeModel, bagInfo.path)
      : bagInfo.typeModel;

    if (
      action.returnType !== bagInfo.returnTypeName ||
      action.returnCardinality !== bagInfo.returnCardinality ||
      !isEqual(action.returnTypeModel, resolvedTypeModel)
    ) {
      action.returnType = bagInfo.returnTypeName as BackEndValueType;
      action.returnCardinality = bagInfo.returnCardinality as ECardinality;

      if (knownTypes.includes(action.returnType?.toLowerCase())) {
        action.returnTypeModel = undefined;
      } else {
        action.returnTypeModel = resolvedTypeModel as IKType;
      }

      action.tag = bagKeyValue;

      this.props.onSaveAction(action, ESdChangeReason.ChangeSelectedDiByTools);
    }
  };

  handleBagKeySelect = (value: string | null): void => {
    // Clearing the field is reported through `onClear`, which writes the same parameter. The
    // selectbox also reports the empty selection here, and acting on it would save twice and
    // put a null in the tree on the way past.
    if (value === null || value === undefined) return;
    const params = _.cloneDeep(this.props.parameters);
    const keyParam = params.find((p) => p.name === "key");
    if (!keyParam) return;
    if (!keyParam.symbol) keyParam.symbol = KMainFunctions.getDefaultSymbol(keyParam.Typename);
    keyParam.symbol.fixedValue = value;
    keyParam.symbol.category = ESymbolCategory.fixed;

    const bags = this.props.sd.bags;
    const bagInfo = bags[value];

    const resolvedTypeModel = bagInfo.path
      ? this.getTypeModelByPath(bagInfo.typeModel, bagInfo.path)
      : bagInfo.typeModel;

    const action: IAction = _.cloneDeep(this.props.action);
    action.parameters = params;
    action.returnType = bagInfo.returnTypeName as BackEndValueType;
    action.returnCardinality = bagInfo.returnCardinality as ECardinality;

    if (knownTypes.includes(action.returnType?.toLowerCase())) {
      action.returnTypeModel = undefined;
    } else {
      action.returnTypeModel = resolvedTypeModel as IKType;
    }

    action.tag = value;

    this.props.onSaveAction(action, ESdChangeReason.ActionParameterFixedValueChanged);
  };

  handleBagKeyClear = () => {
    const params = _.cloneDeep(this.props.parameters);
    const keyParam = params.find((p) => p.name === "key");
    if (!keyParam) return;
    if (!keyParam.symbol) keyParam.symbol = KMainFunctions.getDefaultSymbol(keyParam.Typename);
    keyParam.symbol.fixedValue = undefined;
    keyParam.symbol.category = ESymbolCategory.fixed;
    const action: IAction = _.cloneDeep(this.props.action);
    action.parameters = params;
    action.returnType = "Any";
    action.returnCardinality = ECardinality.Any;
    // The tag is the key; left behind, it would label the result with a key it no longer reads.
    action.tag = undefined;

    this.props.onSaveAction(action, ESdChangeReason.ChangeSelectedDiByTools);
  };

  getBagKeyValue = (parameters: IActionParameter[]): string => {
    const keyParam = parameters.find((p) => p.name === "key");
    return keyParam?.symbol?.fixedValue;
  };

  renderKey() {
    return (
      <div>
        <div className="k_Regular-G1-Left kuika_rightPanelDIName" style={{ marginBottom: "6px", marginTop: "8px" }}>
          <ActionParameterTitle parameter={{ name: "Bag Key", Typename: "String", cardinality: ECardinality.One }} />
        </div>
        <KuikaSelectbox
          searchable
          size="sm"
          allowClear={
            !this.props.sd.isDynamicTypesLoading &&
            !!this.getBagKeyValue(this.props.parameters) &&
            this.getBagKeyValue(this.props.parameters)?.trim?.() !== ""
          }
          style={{ width: "100%" }}
          value={this.getBagKeyValue(this.props.parameters)}
          onVisibleChange={(open: boolean): void => {
            if (open) this.props.sd.fetchDynamicTypeKeys();
          }}
          onChange={this.handleBagKeySelect}
          onClear={this.handleBagKeyClear}
          options={(Object.keys(this.props.sd.bags) || []).map((k) => ({ label: k, value: k }))}
          placeholder="Select Bag Key"
          className="kuika_add_actions_inputs_size"
          notFoundContent={this.props.sd.isDynamicTypesLoading ? <SymbolDropdownLoading /> : undefined}
        />
      </div>
    );
  }

  render() {
    return <div className="kuika_property-grid-column">{this.renderKey()}</div>;
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.ActionParameterFixedValueChanged
    );
  };
}

const getFromBagSymbolsGroup = withContext<IGetFromBagSymbolsGroupProps>(
  withSD<IGetFromBagSymbolsGroupProps>(GetFromBagSymbolsGroup)
);
export { getFromBagSymbolsGroup as GetFromBagSymbolsGroup };
